import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "@/components/ui/sonner";
import { apiClient } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isAuthenticated = Boolean(user);

  const clearAuth = useCallback(() => {
    setUser(null);
  }, []);

  const login = useCallback(async (email, password) => {
    setIsSubmitting(true);
    try {
      const response = await apiClient.post("/auth/login", { email, password });
      setUser(response.data.user);
      toast.success("Connexion réussie");
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || (error.request ? "Serveur injoignable ou session bloquée par le navigateur." : "Impossible de se connecter.");
      toast.error(message);
      return { success: false, message };
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const logout = useCallback(() => {
    apiClient.post("/auth/logout").catch(() => {});
    clearAuth();
    toast.success("Déconnexion effectuée");
  }, [clearAuth]);

  useEffect(() => {
    const syncUser = async () => {
      try {
        const response = await apiClient.get("/auth/me");
        setUser(response.data);
      } catch (error) {
        const status = error.response?.status;
        if (status === 401 || status === 403) {
          clearAuth();
        }
      } finally {
        setIsLoading(false);
      }
    };

    syncUser();
  }, [clearAuth]);

  const value = useMemo(
    () => ({ user, isAuthenticated, isLoading, isSubmitting, login, logout }),
    [user, isAuthenticated, isLoading, isSubmitting, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth doit être utilisé à l'intérieur de AuthProvider");
  }
  return context;
};
